import UserShortCard from '@components/UserShortCard';
import { Empty, Form, Input, Pagination, Spin } from 'antd';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import css from './TribesMembersList.module.less';
import { useStore } from 'effector-react';
import tribesMembers, {
  getList,
  banMember,
  unbanMember,
  reset,
  setPagination,
} from '@stores/tribesMembers';
import useTranslate from '@hooks/useTranslate';
import ROLES from '@constants/roles';
import authStore from '@stores/auth';
import componentContent from '../TribesPage.content.json';
import { pageSizeOptions } from '@constants/pagination';
import { parseTribeMembersFilters } from '@helpers/tribesFilters';
import useQueryFilters from '@hooks/useQueryFilters';

const TribesMembersList: FunctionComponent = () => {
  const auth = useStore(authStore);

  const isAdmin =
    auth?.user?.role === ROLES.ADMIN ||
    auth?.user?.role === ROLES.COMMUNITY_MANAGER ||
    auth?.user?.role === ROLES.MODERATOR;

  const content = useTranslate(componentContent);
  const { tribeId } = useParams<any>();
  const { list, pagination, preloaders } = useStore(tribesMembers);
  const { filters, setFilters } = useQueryFilters(parseTribeMembersFilters);
  const [nickname, setNickname] = useState(filters.nickname);

  useEffect(() => setNickname(filters.nickname), [filters.nickname]);

  const [actionAccountId, setActionAccountId] = useState<string | null>(null);

  const isListLoading = !!preloaders.getList;
  const isActionLoading = !!preloaders.banMember || !!preloaders.unbanMember;

  const paginationChange = useCallback(
    (current: number, pageSize?: number) => {
      setPagination({ ...pagination, current, pageSize });
      getList({ tribeId, ...filters });
    },
    [tribeId, pagination, filters],
  );

  useEffect(() => {
    setPagination({ ...tribesMembers.getState().pagination, current: 1 });
    getList({ tribeId, ...filters });
  }, [tribeId, filters]);

  useEffect(() => () => reset(), []);

  const onBan = async (accountId: string) => {
    if (!tribeId) return;
    if (isActionLoading) return;

    setActionAccountId(accountId);

    try {
      await banMember({ tribeId, accountId });
    } finally {
      setActionAccountId(null);
    }
  };

  const onUnban = async (accountId: string) => {
    if (!tribeId) return;
    if (isActionLoading) return;

    setActionAccountId(accountId);

    try {
      await unbanMember({ tribeId, accountId });
    } finally {
      setActionAccountId(null);
    }
  };

  return (
    <>
      <Form
        layout='inline'
        className={css.filters}
      >
        <Form.Item label={content.search_by_nickname}>
          <Input.Search
            aria-label={content.search_by_nickname}
            allowClear
            value={nickname}
            onChange={(event) => setNickname(event.currentTarget.value)}
            onSearch={(value) => setFilters({ ...filters, nickname: value.trim() })}
          />
        </Form.Item>
      </Form>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          onChange={paginationChange}
          showSizeChanger
          showQuickJumper
          pageSizeOptions={pageSizeOptions}
        />
      </div>

      <Spin spinning={isListLoading}>
        {Array.isArray(list) && list.length > 0 ? (
          list.map((item: any) => {
            const account = item.account;
            const accountId = String(account?.id);
            const isOwner = Boolean(item?.role === 'OWNER');
            const isBanned = Boolean(item?.role === 'BANNED');

            return (
              <div
                className={css.root}
                key={accountId}
              >
                <section className={css.content}>
                  <UserShortCard user={account} />

                  {!isOwner && isAdmin && (
                    <div className={css.actions}>
                      {isBanned ? (
                        <a
                          className='clickable'
                          onClick={() => onUnban(accountId)}
                        >
                          {actionAccountId === accountId && isActionLoading ? (
                            <Spin size='small' />
                          ) : (
                            content.return_participant
                          )}
                        </a>
                      ) : (
                        <a
                          className='clickable'
                          onClick={() => onBan(accountId)}
                        >
                          {actionAccountId === accountId && isActionLoading ? (
                            <Spin size='small' />
                          ) : (
                            content.exclude_participant
                          )}
                        </a>
                      )}
                    </div>
                  )}
                </section>
              </div>
            );
          })
        ) : (
          <Empty imageStyle={{ height: '50px' }} />
        )}
      </Spin>

      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          onChange={paginationChange}
          showSizeChanger
          showQuickJumper
          pageSizeOptions={pageSizeOptions}
        />
      </div>
    </>
  );
};

export default TribesMembersList;
